import { useState } from 'react'
import { COLORS, TYPE } from '../tokens'
import SectionLabel from '../SectionLabel'
import FindingDispositionMenu from '../FindingDispositionMenu'
import FindingEditor from '../FindingEditor'
import { mergeEdits } from '../useFindingDisposition'

const DANGER = '#C04A3D'
const AMBER = '#B7791F'

// One finding rendered as an editorial article with:
// - severity + closure status small-caps labels
// - risk badge when hallucination-risk classifier flags high/medium
// - inline disposition menu (Approve / Modify / Reject)
// - inline editor when disposition is 'modified'
// - pull-quote citation with source page + QA note
// - metadata footer (confidence, prior link, disposition)
export default function FindingArticle({ finding, risk, disposition, edits, onSetDisposition, onSetEdits, locked }) {
  const [editing, setEditing] = useState(false)
  const merged = mergeEdits(finding, edits)
  const {
    title, detail, business_impact, severity, closure_status,
    evidence_quote, evidence_location, source_page,
    adjusted_confidence, confidence, confidence_tier,
    citation_verified, parent_finding_id
  } = merged

  const shownConfidence = adjusted_confidence ?? confidence
  const closureLabel = closure_status && closure_status !== 'open'
    ? closure_status.replace('_', ' ').toUpperCase()
    : null

  const riskColor = risk?.level === 'high' ? DANGER : risk?.level === 'medium' ? AMBER : null
  const isRejected = disposition === 'rejected'

  if (isRejected) return null

  return (
    <article style={{
      marginBottom: '48px',
      color: COLORS.bodyText,
      opacity: risk?.level === 'high' && disposition === 'pending' ? 0.85 : 1
    }}>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        {severity && <SectionLabel variant="severity">{severity.toUpperCase()} SEVERITY</SectionLabel>}
        {closureLabel && <SectionLabel variant="accent">{closureLabel}</SectionLabel>}
        {riskColor && (
          <span data-print="hide" style={{
            ...TYPE.smallCaps,
            color: riskColor,
            padding: '2px 8px',
            border: `1px solid ${riskColor}40`,
            borderRadius: '999px',
            background: `${riskColor}0F`
          }}>{risk.level} risk</span>
        )}
        <div data-print="hide" style={{ marginLeft: 'auto' }}>
          {locked ? (
            <span style={{
              ...TYPE.smallCaps,
              color: COLORS.mutedText,
              padding: '4px 10px',
              border: `1px solid ${COLORS.hairline}`,
              borderRadius: '4px'
            }}>Locked · Verified</span>
          ) : (
            <FindingDispositionMenu
              disposition={disposition || 'pending'}
              onChange={(next) => {
                if (next === 'modified') setEditing(true)
                else onSetDisposition?.(next)
              }}
            />
          )}
        </div>
      </div>
      <h3 style={{ fontSize: '22px', fontWeight: 500, lineHeight: 1.25, margin: '0 0 12px' }}>{title}</h3>
      {edits && (
        <div style={{ ...TYPE.smallCaps, color: AMBER, marginBottom: '8px' }}>
          Advisor-edited
        </div>
      )}
      <p style={{ ...TYPE.body, marginBottom: business_impact ? '10px' : '20px' }}>{detail}</p>
      {business_impact && (
        <p style={{ ...TYPE.body, color: COLORS.mutedText, marginBottom: '20px' }}>
          <SectionLabel variant="severity" style={{ marginRight: '8px' }}>Business impact</SectionLabel>
          {business_impact}
        </p>
      )}
      {evidence_quote && (
        <blockquote style={{
          fontStyle: 'italic',
          borderLeft: `4px solid ${COLORS.retentionBg}`,
          paddingLeft: '16px',
          margin: '16px 0',
          color: COLORS.bodyText
        }}>
          &ldquo;{evidence_quote}&rdquo;
          <footer style={{ ...TYPE.supporting, fontStyle: 'normal', color: COLORS.mutedText, marginTop: '8px' }}>
            {source_page != null ? `Page ${source_page}` : 'Page —'}
            {evidence_location ? ` · ${evidence_location}` : ''}
            {citation_verified
              ? ' · Citation verified by QA'
              : <span data-print="hide"> · Citation unverified</span>}
          </footer>
        </blockquote>
      )}
      <div data-print="hide" style={{ ...TYPE.supporting, color: COLORS.mutedText, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <span>Confidence {shownConfidence ?? '—'}{confidence_tier ? ` · ${confidence_tier}` : ''}</span>
        {parent_finding_id && <span>Continues finding from prior review</span>}
      </div>

      {editing && (
        <FindingEditor
          finding={finding}
          edits={edits}
          onCancel={() => setEditing(false)}
          onSave={async (nextEdits) => {
            await onSetEdits?.(nextEdits)
            setEditing(false)
          }}
        />
      )}
    </article>
  )
}
